import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckSquare, Target, CalendarDays, Clock, AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePanel } from "@/context/PanelContext";
import { localISODate, weekdayShort } from "@/lib/utils-tg";

const TYPES = [
  { v: "task", icon: CheckSquare, label: "Task" },
  { v: "goal", icon: Target, label: "Goal" },
  { v: "meeting", icon: CalendarDays, label: "Meeting" },
  { v: "activity", icon: Clock, label: "Time entry" },
  { v: "issue", icon: AlertTriangle, label: "Issue" },
  { v: "approval", icon: ShieldCheck, label: "Approval" },
];

// Time-horizon options for new Goals — must match labels in GoalsPage.
const HORIZONS_NEW = [
  { id: "week",    label: "1-week sprint",  days: 7   },
  { id: "twoweek", label: "2-week sprint",  days: 14  },
  { id: "month",   label: "30-day goal",    days: 30  },
  { id: "quarter", label: "90-day goal",    days: 90  },
  { id: "year",    label: "1-year goal",    days: 365 },
];

export default function NewModal({ open, onOpenChange, defaultType = "task" }) {
  const [type, setType] = useState(defaultType);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [departments, setDepartments] = useState([]);
  const { triggerRefresh } = usePanel();

  useEffect(() => {
    if (open) {
      setType(defaultType);
      const today = localISODate();
      setForm({ date: today });
      api.get("/departments/mine").then((r) => setDepartments(r.data)).catch(() => {});
    }
  }, [open, defaultType]);

  const currentDept = departments.find((d) => d.id === form.department_id);
  const categories = currentDept?.categories || [];

  const submit = async () => {
    setBusy(true);
    try {
      if (type === "task") {
        await api.post("/tasks", { title: form.title, description: form.description || "" });
      } else if (type === "goal") {
        const today = new Date();
        const horizon = HORIZONS_NEW.find((h) => h.id === (form.horizon || "twoweek")) || HORIZONS_NEW[1];
        const start = today.toISOString().slice(0, 10);
        const end = new Date(today.getTime() + horizon.days * 86400000).toISOString().slice(0, 10);
        await api.post("/goals", {
          title: form.title,
          description: form.description || "",
          sprint_start: start,
          sprint_end: end,
        });
      } else if (type === "meeting") {
        await api.post("/meetings", { title: form.title, notes: form.notes || "" });
      } else if (type === "activity") {
        const payload = {
          title: form.title,
          description: form.description || form.title,
          date: form.date,
          start_time: form.start_time || null,
          end_time: form.end_time || null,
          department_id: form.department_id || null,
          category_id: form.category_id || null,
        };
        if (form.duration) payload.duration_minutes = parseDuration(form.duration);
        await api.post("/activities", payload);
      } else if (type === "issue") {
        await api.post("/issues", { title: form.title, description: form.description || "" });
      } else if (type === "approval") {
        await api.post("/approvals", {
          title: form.title,
          amount: form.amount ? parseFloat(form.amount) : null,
          description: form.description || "",
        });
      }
      toast.success(`${TYPES.find((x) => x.v === type)?.label} created`);
      triggerRefresh();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Create something</DialogTitle>
          <DialogDescription>Pick a type and fill the essentials.</DialogDescription>
        </DialogHeader>

        <Tabs value={type} onValueChange={setType}>
          <TabsList className="grid grid-cols-3 sm:grid-cols-6 gap-1 bg-slate-100 p-1 rounded-xl">
            {TYPES.map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                className="data-[state=active]:bg-white data-[state=active]:text-emerald-600 rounded-lg text-xs"
                data-testid={`new-type-${t.v}`}
              >
                <t.icon className="h-3.5 w-3.5 mr-1" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {type === "activity" ? "Activity description" : "Title"}
            </label>
            <input
              className="tg-input mt-1"
              value={form.title || ""}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={type === "activity" ? "Short description of what you did" : "What is it?"}
              data-testid="new-title-input"
              autoFocus
            />
          </div>

          {type === "activity" && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Date</label>
                  <input
                    type="date"
                    className="tg-input mt-1"
                    value={form.date || ""}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="new-date-input"
                  />
                  {form.date && (
                    <div className="text-[10px] text-emerald-700 font-semibold mt-1" data-testid="new-date-weekday">
                      {weekdayShort(form.date)}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Start</label>
                  <input
                    type="time"
                    className="tg-input mt-1"
                    value={form.start_time || ""}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    data-testid="new-start-time"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">End</label>
                  <input
                    type="time"
                    className="tg-input mt-1"
                    value={form.end_time || ""}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    data-testid="new-end-time"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Department</label>
                  <select
                    className="tg-input mt-1"
                    value={form.department_id || ""}
                    onChange={(e) => setForm({ ...form, department_id: e.target.value, category_id: "" })}
                    data-testid="new-dept-select"
                  >
                    <option value="">— Select —</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Work category</label>
                  <select
                    className="tg-input mt-1"
                    value={form.category_id || ""}
                    onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                    disabled={!form.department_id}
                    data-testid="new-cat-select"
                  >
                    <option value="">— Select —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Duration (optional)</label>
                <input
                  className="tg-input mt-1"
                  value={form.duration || ""}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  placeholder="e.g. 1h30, 90m, or 1:30"
                  data-testid="new-duration-input"
                />
                <div className="text-[10px] text-slate-400 mt-1">
                  Enter hours &amp; minutes (1h30 / 90m / 1:30). If left blank, we use Start &amp; End above.
                </div>
              </div>
            </>
          )}

          {type === "approval" && (
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Amount (optional)</label>
              <input
                className="tg-input mt-1"
                value={form.amount || ""}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="120.00"
                data-testid="new-amount-input"
              />
            </div>
          )}

          {type === "meeting" ? (
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Notes</label>
              <textarea
                className="tg-input mt-1 min-h-[120px] resize-none"
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Paste meeting notes here. AI can extract tasks later."
                data-testid="new-notes-input"
              />
            </div>
          ) : type !== "activity" ? (
            <>
              {type === "goal" && (
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Horizon</label>
                  <select
                    className="tg-input mt-1"
                    value={form.horizon || "twoweek"}
                    onChange={(e) => setForm({ ...form, horizon: e.target.value })}
                    data-testid="new-goal-horizon"
                  >
                    {HORIZONS_NEW.map((h) => (
                      <option key={h.id} value={h.id}>{h.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Description</label>
                <textarea
                  className="tg-input mt-1 min-h-[80px] resize-none"
                  value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Add context (optional)"
                  data-testid="new-description-input"
                />
              </div>
            </>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={() => onOpenChange(false)} className="tg-btn-secondary" data-testid="new-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !form.title}
            className="tg-btn-primary disabled:opacity-50"
            data-testid="new-submit"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Create
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function parseDuration(input) {
  if (!input) return 30;
  const s = String(input).trim().toLowerCase();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  // hh:mm form, e.g. 1:30
  const colon = s.match(/^(\d+):(\d{1,2})$/);
  if (colon) return parseInt(colon[1], 10) * 60 + parseInt(colon[2], 10);
  const h = s.match(/(\d+)\s*h/);
  const m = s.match(/(\d+)\s*m/);
  let total = 0;
  if (h) total += parseInt(h[1], 10) * 60;
  if (m) total += parseInt(m[1], 10);
  if (!total) {
    const both = s.match(/^(\d+)h(\d+)$/);
    if (both) total = parseInt(both[1], 10) * 60 + parseInt(both[2], 10);
  }
  return total || 30;
}
